import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'

import { MCP, scopeLabel } from '@brickup/shared/locales/de/mcp'
import { useGrantMcpConsent, useMcpConsent } from '@/api/mcp'
import { LoadingIndicator } from '@/components/base'
import { CtaButton } from '@/components/CtaButton'
import {
  PublicPageCard,
  PublicPageCardMessage,
  PublicPageHeading,
  PublicPageText,
} from '@/components/Public/PublicPageCard'
import { useMutationErrorSnackbar } from '@/hooks/use-mutation-error-snackbar'
import { requireAuth } from '@/utils/require-auth'

export const Route = createFileRoute('/mcp/consent')({
  validateSearch: (search: Record<string, unknown>): { request: string } => ({
    request: typeof search.request === 'string' ? search.request : '',
  }),
  // Signed in before anything is shown: the account that grants is the account
  // the token is issued for, and there is no point rendering the question to
  // somebody who cannot answer it.
  beforeLoad: async ({ context, location, search }) => {
    // `location.search` is the parsed object, which `validateSearch` always
    // fills — the request itself is what may be missing, and an address
    // arrived at without one is not this page.
    if (!search.request) throw redirect({ to: '/' })
    await requireAuth(context.queryClient, location.href)
  },
  component: McpConsentScreen,
})

function McpConsentScreen() {
  const { request } = Route.useSearch()
  const { data, isLoading, isError } = useMcpConsent(request)
  const grant = useGrantMcpConsent()
  const showError = useMutationErrorSnackbar()
  const [denied, setDenied] = useState(false)

  if (isLoading) return <LoadingIndicator layout="page" />

  // Said rather than done: the browser arrived here on an OS-level redirect
  // from the client, so `window.close()` is refused — nothing opened this
  // window. Telling the operator they may close it is what is left.
  if (denied) {
    return <PublicPageCardMessage title={MCP.denied} />
  }

  if (isError || !data) {
    // Reached when the request is expired or malformed, never when the
    // operator refused — so it says what to do, not what they supposedly did.
    return <PublicPageCardMessage variant="error" title={MCP.consentRefused} />
  }

  return (
    <PublicPageCard>
      <PublicPageHeading title={MCP.consentTitle(data.clientName)} />
      <PublicPageText>{MCP.consentIntro}</PublicPageText>

      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {data.allScopes.map(scope => {
          const asked = data.scopes.includes(scope)
          return (
            <li key={scope} className="flex flex-col gap-0.5">
              <span className="flex flex-row items-baseline gap-2 text-sm font-medium text-neutral-900">
                {scopeLabel(scope)}
                {asked ? null : (
                  <span className="text-xs font-normal text-neutral-400">
                    {MCP.notAsked}
                  </span>
                )}
              </span>
              <span className="text-xs text-neutral-500">
                {MCP.scopeHints[scope]}
              </span>
            </li>
          )
        })}
      </ul>

      <div className="flex flex-row gap-2">
        <CtaButton
          label={MCP.allow}
          icon={null}
          layout="fill"
          loading={grant.isPending}
          onClick={() =>
            grant.mutate(request, {
              // A full navigation, not the router: the address belongs to the
              // client that started this, and it is usually not a web page.
              onSuccess: data => window.location.replace(data.redirectTo),
              onError: error => showError(error, MCP.consentRefused),
            })
          }
        />
        <CtaButton
          tone="outline"
          label={MCP.deny}
          icon={null}
          disabled={grant.isPending}
          onClick={() => setDenied(true)}
        />
      </div>
    </PublicPageCard>
  )
}
